import { getSectorInfo } from "../../helpers/meetstandaard/sectorCatalog";
import {
  sectorContextGroupsFor,
  dependsOnSatisfied,
} from "../../helpers/dataHelpers/sectorContextQuestions";

// AI-CONTEXT: See docs/features/sector-context-questions.md
// AI-CONTEXT: See docs/decisions.md (ADR-031 + its 2026-08-17 amendment)

/**
 * The chips for one question. Local rather than NodeEditor's ChipRow: that one
 * isn't exported and has no disabled state, and a chip that looks clickable but
 * silently does nothing is its own bug (docs/pitfalls.md#ui-removed-prop-resurrects-default).
 * The classes are shared with NodeEditor so the band looks native.
 *
 * @param {object} props
 * @param {string} props.label - The question, as the group's accessible name
 * @param {Array<{value: string, label: string}>} props.options - Chips to render, in order
 * @param {string} [props.value] - The stored answer, if any
 * @param {boolean} props.canEdit - false renders the chips disabled, not merely inert
 * @param {(value: string|null) => void} props.onPick - null means "clear this answer"
 */
const QuestionChips = ({ label, options, value, canEdit, onPick }) => (
  // A labelled group: the chips are mutually exclusive answers to one question,
  // and without the name a screen reader reads eleven identical "Ja" buttons.
  <div className="node-editor-chips" role="group" aria-label={label}>
    {options.map((option) => {
      const selected = option.value === value;
      return (
        <button
          key={option.value}
          type="button"
          className={`node-editor-chip${selected ? " is-on" : ""}`}
          disabled={!canEdit}
          // Clicking the selected chip clears it. Without that a mis-click is
          // permanent, and "unanswered" is a real state here: the benchmark
          // treats an absent answer as absent, not as a 'nee'.
          onClick={() => onPick(selected ? null : option.value)}
        >
          {option.label}
        </button>
      );
    })}
  </div>
);

/**
 * The options to render for a question, including the stored answer even when
 * the config no longer offers it.
 *
 * A narrowing control must never blank an out-of-scope stored value — the next
 * interaction would write the blank over real data (ADR-031 rule 4,
 * docs/patterns.md#data-taxonomy rule 5). The raw value doubles as its own
 * label; there is nothing better to show for a value we no longer define.
 *
 * @param {{options: Array<{value: string, label: string}>}} question
 * @param {string} [value] - The stored answer.
 * @returns {Array<{value: string, label: string}>}
 */
const optionsIncludingStored = (question, value) => {
  if (!value || question.options.some((o) => o.value === value)) return question.options;
  return [...question.options, { value, label: value }];
};

/**
 * Sector-specific context questions for the Theory of Change.
 *
 * Organisation-level characteristics the tenant self-reports once, so the
 * benchmark can group organisations on them. Purely presentational: it reads the
 * config, decides what to show, and reports every change through `onAnswer`.
 * The config's groups are flattened into one list — their headings read as
 * noise rather than structure.
 * The write — dot-paths, and clearing dependent answers — belongs to the parent.
 *
 * Renders `null` when nothing applies. Deliberately fails CLOSED: a tenant with
 * no sector sees no band at all, unlike the mechanism dropdown which shows
 * everything when unscoped. See the 2026-08-17 amendment to ADR-031.
 *
 * @param {object} props
 * @param {string[]} [props.sectors] - CompagnyMeta.SelectedSectors
 * @param {Record<string, Record<string, string>>} [props.answers] - CompagnyMeta.SectorContext, keyed per sector slug
 * @param {boolean} props.canEdit - strategy.edit; false renders the chips read-only
 * @param {(sector: string, questionId: string, value: string|null) => void} props.onAnswer - null clears the answer
 */
const SectorContextBand = ({ sectors, answers, canEdit, onAnswer }) => {
  const blocks = sectorContextGroupsFor(sectors);

  // No sector, or a sector without questions: render nothing at all — no
  // heading, no empty card.
  if (blocks.length === 0) return null;

  // Only name the sector when more than one contributes questions; with a
  // single block the heading would just repeat what the page already says.
  const showSectorName = blocks.length > 1;

  return (
    <div className="sectorctx-band">
      {blocks.map(({ sector, groups }) => {
        const given = (answers && answers[sector]) || {};

        return (
          <div className="sectorctx-sector" key={sector}>
            {/* Only when more than one sector contributes questions — otherwise
                the name repeats what the page already says. */}
            {showSectorName && (
              <div className="sectorctx-sector-label">{getSectorInfo(sector).label}</div>
            )}
            {groups
              .flatMap((group) => group.questions)
              .filter((question) => dependsOnSatisfied(question, given))
              .map((question) => (
                <div className="sectorctx-question" key={question.id}>
                  <div className="node-editor-label">{question.label}</div>
                  <QuestionChips
                    label={question.label}
                    options={optionsIncludingStored(question, given[question.id])}
                    value={given[question.id]}
                    canEdit={canEdit}
                    onPick={(value) => onAnswer(sector, question.id, value)}
                  />
                </div>
              ))}
          </div>
        );
      })}
    </div>
  );
};

export default SectorContextBand;
